import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Fab,
  IconButton,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { customersAPI, transactionsAPI, apiUtils } from '../services/api';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerTransactions, setCustomerTransactions] = useState([]);
  const [customerSummary, setCustomerSummary] = useState({});
  const [formData, setFormData] = useState({
    display_name: '',
    first_name: '',
    last_name: '',
    mobile: '',
    address: '',
    notes: '',
    active: 1
  });
  const [editMode, setEditMode] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await customersAPI.getAll();
      console.log('customers:', response.data);
      
      setCustomers(apiUtils.formatResponse(response) || []);
    } catch (error) {
      console.error('Error loading customers:', error);
      setError('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const loadCustomerDetails = async (customerId) => {
    try {
      // Load customer transactions
      const transactionsResponse = await transactionsAPI.withCustomerId(customerId);
      setCustomerTransactions(transactionsResponse.data || []);

      // Load customer summary
      const summaryResponse = await customersAPI.getSummary(customerId);
      setCustomerSummary(summaryResponse.data || {});
    } catch (error) {
      console.error('Error loading customer details:', error);
      setError('Failed to load customer details');
    }
  };

  const handleAdd = () => {
    setFormData({
      display_name: '',
      first_name: '',
      last_name: '',
      mobile: '',
      address: '',
      notes: '',
      active: 1
    });
    setEditMode(false);
    setOpenDialog(true);
  };

  const handleEdit = (customer) => {
    setFormData({
      display_name: customer.display_name || '',
      first_name: customer.first_name || '',
      last_name: customer.last_name || '',
      mobile: customer.mobile || '',
      address: customer.address || '',
      notes: customer.notes || '',
      active: customer.active || 1
    });
    setSelectedCustomer(customer);
    setEditMode(true);
    setOpenDialog(true);
  };

  const handleView = async (customer) => {
    setSelectedCustomer(customer);
    await loadCustomerDetails(customer.id);
    setOpenViewDialog(true);
  };

  const handleDelete = (customer) => {
    setDeleteConfirm(customer);
  };

  const confirmDelete = async () => {
    try {
      await customersAPI.delete(deleteConfirm.id);
      setDeleteConfirm(null);
      loadCustomers();
    } catch (error) {
      console.error('Error deleting customer:', error);
      setError('Failed to delete customer');
    }
  };

  const handleSubmit = async () => {
    try {
      if (editMode) {
        await customersAPI.update(selectedCustomer.id, formData);
      } else {
        await customersAPI.create(formData);
      }
      setOpenDialog(false);
      loadCustomers();
    } catch (error) {
      console.error('Error saving customer:', error);
      setError('Failed to save customer');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getStatusColor = (active) => {
    return active === 1 ? 'success' : 'error';
  };

  const columns = [
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleView(params.row)}
            color="primary"
          >
            <ViewIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.row)}
            color="secondary"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'display_name',
      headerName: 'Customer Name',
      width: 200,
    },
    {
      field: 'full_name',
      headerName: 'Full Name',
      width: 200,
      valueGetter: (value, row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || 'N/A'
    },
    {
      field: 'mobile',
      headerName: 'Mobile',
      width: 150,
    },
    {
      field: 'address',
      headerName: 'Address',
      width: 200,
    },
    {
      field: 'active',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value === 1 ? 'Active' : 'Inactive'}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: 'total_purchases',
      headerName: 'Total Purchases',
      width: 150,
      renderCell: (params) => (
        `₱${parseFloat(params.value || 0).toLocaleString()}`
      ),
    },
    {
      field: 'last_purchase',
      headerName: 'Last Purchase',
      width: 150,
      renderCell: (params) => (
        params.value ? new Date(params.value).toLocaleDateString() : 'Never'
      ),
    },
  ];

  const transactionColumns = [
    {
      field: 'txn_no',
      headerName: 'Transaction #',
      width: 120,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      renderCell: (params) => `₱${parseFloat(params.value || 0).toFixed(2)}`,
    },
    {
      field: 'payment_type',
      headerName: 'Payment',
      width: 100,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'COMPLETED' ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'trans_date',
      headerName: 'Date',
      width: 150,
      renderCell: (params) => new Date(params.value).toLocaleDateString(),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading customers...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 3, flexShrink: 0 }}>
        <Typography variant="h4" component="h1">
          Customers
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper 
        sx={{ 
          height: 'calc(100vh - 140px)', 
          width: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <DataGrid
          rows={customers}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          loading={loading}
          disableSelectionOnClick
          autoHeight={false}
          disableVirtualization={false}
          sx={{
            height: '100%',
            width: '100%',
            border: 'none',
            '& .MuiDataGrid-main': {
              overflow: 'auto',
            },
            '& .MuiDataGrid-virtualScroller': {
              overflow: 'auto !important',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid',
              borderColor: 'divider',
              minHeight: '52px',
              backgroundColor: 'background.paper',
              justifyContent: 'flex-start',
              '& .MuiTablePagination-root': {
                marginLeft: 0,
              },
              '& .MuiTablePagination-toolbar': {
                justifyContent: 'flex-start',
              },
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'background.paper',
              borderBottom: '2px solid',
              borderColor: 'divider',
            },
          }}
        />
      </Paper>

      {/* Add/Edit Customer Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Customer' : 'Add New Customer'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Display Name"
                value={formData.display_name}
                onChange={(e) => handleInputChange('display_name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mobile"
                value={formData.mobile}
                onChange={(e) => handleInputChange('mobile', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={3}
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.active}
                  label="Status"
                  onChange={(e) => handleInputChange('active', e.target.value)}
                >
                  <MenuItem value={1}>Active</MenuItem>
                  <MenuItem value={0}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Customer Details Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon />
            Customer Details: {selectedCustomer?.display_name}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedCustomer && (
            <Grid container spacing={3}>
              {/* Customer Information */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Customer Information
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon color="primary" />
                        <Typography>{selectedCustomer.display_name}</Typography>
                      </Box>
                      {(selectedCustomer.first_name || selectedCustomer.last_name) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon color="primary" />
                          <Typography>{`${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim()}</Typography>
                        </Box>
                      )}
                      {selectedCustomer.mobile && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon color="primary" />
                          <Typography>{selectedCustomer.mobile}</Typography>
                        </Box>
                      )}
                      {selectedCustomer.address && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationIcon color="primary" />
                          <Typography>{selectedCustomer.address}</Typography>
                        </Box>
                      )}
                      {selectedCustomer.notes && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Notes: {selectedCustomer.notes}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={selectedCustomer.active === 1 ? 'Active' : 'Inactive'}
                          color={getStatusColor(selectedCustomer.active)}
                          size="small"
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Sales Summary */}
              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Sales Summary
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                          <MoneyIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                          <Typography variant="h5" fontWeight="bold">
                            ₱{parseFloat(customerSummary.total_purchases || 0).toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Purchases
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                          <ReceiptIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                          <Typography variant="h5" fontWeight="bold">
                            {customerSummary.total_transactions || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Transactions
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                          <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                          <Typography variant="h5" fontWeight="bold">
                            ₱{parseFloat(customerSummary.average_purchase || 0).toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Average Purchase
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Transaction History */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Transaction History
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    {customerTransactions.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Transaction #</TableCell>
                              <TableCell>Amount</TableCell>
                              <TableCell>Payment</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Date</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {customerTransactions.slice(0, 10).map((transaction) => (
                              <TableRow key={transaction.id}>
                                <TableCell>{transaction.txn_no}</TableCell>
                                <TableCell>₱{parseFloat(transaction.amount || 0).toFixed(2)}</TableCell>
                                <TableCell>{transaction.payment_type}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={transaction.status}
                                    color={transaction.status === 'COMPLETED' ? 'success' : 'default'}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>{new Date(transaction.trans_date).toLocaleDateString()}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        No transactions found for this customer
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete customer "{deleteConfirm?.display_name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for Add Customer */}
      <Fab
        color="primary"
        aria-label="add customer"
        onClick={handleAdd}
        sx={{
          position: 'fixed',
          bottom: 90,
          right: 24,
          zIndex: 1000,
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default Customers;
